import { useQuery } from "@tanstack/react-query"
import type { ColumnDef } from "@tanstack/react-table"
import { Briefcase, Plus, Search } from "lucide-react"
import * as React from "react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { type Job, jobsApi } from "@/api/jobs"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { PageHeader } from "@/components/dashboard/page-header"
import { DataTable } from "@/components/shared/data-table"
import { EmptyState } from "@/components/shared/empty-state"
import { formatDate } from "@/lib/utils"

const STATUS_FILTERS = [
  { v: undefined, label: "Tümü" },
  { v: "open", label: "Açık" },
  { v: "draft", label: "Taslak" },
  { v: "closed", label: "Kapalı" },
] as const

const EMPLOYMENT_LABEL: Record<Job["employment_type"], string> = {
  full_time: "Tam zamanlı",
  part_time: "Yarı zamanlı",
  contract: "Sözleşmeli",
  internship: "Stajyer",
}

export function JobsPage() {
  const [params, setParams] = useSearchParams()
  const status = params.get("status") ?? undefined
  const [search, setSearch] = React.useState("")
  const nav = useNavigate()

  const q = useQuery({
    queryKey: ["jobs", { status }],
    queryFn: () => jobsApi.list({ status }),
  })

  const rows = React.useMemo(() => {
    const all = q.data?.data ?? []
    if (!search.trim()) return all
    const s = search.trim().toLowerCase()
    return all.filter(
      (j) =>
        j.title.toLowerCase().includes(s) ||
        (j.location?.toLowerCase().includes(s) ?? false),
    )
  }, [q.data, search])

  const columns = React.useMemo<ColumnDef<Job>[]>(
    () => [
      {
        accessorKey: "title",
        header: "İlan",
        cell: ({ row }) => (
          <div>
            <div className="font-medium">{row.original.title}</div>
            <div className="text-xs text-muted-foreground">
              {row.original.location ?? "Konum belirtilmemiş"}
            </div>
          </div>
        ),
      },
      {
        accessorKey: "employment_type",
        header: "Tip",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {EMPLOYMENT_LABEL[row.original.employment_type]}
          </span>
        ),
      },
      {
        accessorKey: "status",
        header: "Durum",
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: "created_at",
        header: "Oluşturuldu",
        cell: ({ row }) => (
          <span className="text-xs text-muted-foreground">
            {formatDate(row.original.created_at)}
          </span>
        ),
      },
    ],
    [],
  )

  return (
    <>
      <PageHeader
        title="İlanlar"
        description="Tüm pozisyonlar — durum filtreleri ve arama ile hızlıca süz."
        action={
          <Link to="/dashboard/jobs/new">
            <Button>
              <Plus className="size-4" /> Yeni ilan
            </Button>
          </Link>
        }
      />
      <div className="space-y-4 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {STATUS_FILTERS.map((f) => (
              <Button
                key={f.label}
                size="sm"
                variant={status === f.v ? "default" : "outline"}
                onClick={() => {
                  const next = new URLSearchParams(params)
                  if (f.v) next.set("status", f.v)
                  else next.delete("status")
                  setParams(next)
                }}
              >
                {f.label}
              </Button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Başlık veya konum ara…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          data={rows}
          isLoading={q.isLoading}
          onRowClick={(j) => nav(`/dashboard/jobs/${j.id}`)}
          empty={
            <EmptyState
              icon={Briefcase}
              title={search || status ? "Eşleşen ilan yok" : "Henüz ilan yok"}
              description={
                search || status
                  ? "Filtre veya aramayı değiştir."
                  : "İlk iş ilanını oluştur, ekibinin alım süreci buradan akmaya başlasın."
              }
              action={
                !search && !status ? (
                  <Link to="/dashboard/jobs/new">
                    <Button>
                      <Plus className="size-4" /> Yeni ilan
                    </Button>
                  </Link>
                ) : null
              }
            />
          }
        />
      </div>
    </>
  )
}

function StatusBadge({ status }: { status: Job["status"] }) {
  const map: Record<Job["status"], { variant: "default" | "secondary" | "outline" | "success"; label: string }> = {
    open: { variant: "success", label: "Açık" },
    draft: { variant: "secondary", label: "Taslak" },
    closed: { variant: "outline", label: "Kapalı" },
  }
  const cfg = map[status]
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>
}
